import { useEffect, useState } from "react";
import { QrCode, Download, Copy, CheckCircle2 } from "lucide-react";
import { splinkQrFor } from "@/lib/api";

/**
 * Print-Ready QR card — asks SpLink for a splink.pro/<slug> for the given
 * PRODUCTION URL, then renders a print-ready card with the QR image, the
 * short link, Copy + PNG + SVG download buttons. Every scan flows through
 * SpLink analytics.
 *
 * Props:
 *   productionUrl — the URL a scanner should land on (must be websitetech.pro/*
 *                     for splink to accept, since preview hosts return 422)
 *   title         — visible heading on the card
 *   subtitle      — one-liner beneath the heading
 *   testId        — optional dom test-id suffix
 */
export default function LitLabQrCard({ productionUrl, title, subtitle, testId }) {
  const [qr, setQr] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!productionUrl) return;
    splinkQrFor(productionUrl).then((r) => {
      if (r && r.qr_png) setQr(r);
    }).catch(() => setQr(null));
  }, [productionUrl]);

  if (!qr || !qr.qr_png) return null;

  const copy = () => {
    navigator.clipboard.writeText(qr.short_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="rounded-2xl border border-sky-200 bg-white p-5 shadow-sm flex gap-5 items-center flex-wrap"
      data-testid={testId ? `litlab-qr-${testId}` : "litlab-qr"}>
      <div className="flex-shrink-0 rounded-lg bg-white border border-slate-200 p-2 w-28 h-28">
        <img src={qr.qr_png} alt={`QR — ${title || "L.I.T. Labs"}`}
          className="w-full h-full object-contain" />
      </div>
      <div className="flex-1 min-w-[240px]">
        <div className="text-[10px] uppercase tracking-[0.3em] text-sky-700 font-bold inline-flex items-center gap-1.5">
          <QrCode className="w-3 h-3" /> {title || "Print-Ready QR"}
        </div>
        {subtitle && (
          <div className="mt-2 litlab-serif text-lg font-bold text-slate-900 leading-snug">
            {subtitle}
          </div>
        )}
        <div className="mt-1 text-sm text-slate-600">
          Print on handouts, conference posters or business cards. Every scan is tracked in our SpLink analytics.
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <div className="font-mono text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded px-2 py-1.5">
            {qr.short_url}
          </div>
          <button onClick={copy}
            data-testid={testId ? `litlab-qr-copy-${testId}` : "litlab-qr-copy"}
            className="inline-flex items-center gap-1 text-xs bg-sky-700 hover:bg-sky-600 text-white rounded px-2 py-1.5 font-bold">
            {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </button>
          <a href={qr.qr_png} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs border border-sky-300 hover:border-sky-500 text-sky-700 rounded px-2 py-1.5 font-bold">
            <Download className="w-3 h-3" /> PNG
          </a>
          <a href={qr.qr_svg} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs border border-sky-300 hover:border-sky-500 text-sky-700 rounded px-2 py-1.5 font-bold">
            <Download className="w-3 h-3" /> SVG
          </a>
        </div>
      </div>
    </div>
  );
}
